<template>
  <v-dialog v-model="isShowing" max-width="750px" persistent>
    <v-card>
      <v-card-title>
            <span class="headline">
              {{ $vuetify.t('$vuetify.AdminUsers.csvTitle') }}
            </span>
      </v-card-title>

      <v-card-text>
        <v-layout row wrap>
          <v-flex sm12 md6>
            <p>
              {{ $vuetify.t('$vuetify.AdminUsers.csvInstructions') }}
            </p>
          </v-flex>
          <v-flex class="full-width" md6>
            <div class="d-flex justify-center">
              <upload-btn
                name="csvFile"
                class="csv-button flex-grow-1 b100"
                :disabled="csvUsers != null"
                :error-messages="errors.collect('csvFile')"
                :title="this.$vuetify.t('$vuetify.AdminUsers.csvFilePicker')"
                accept="text/csv*"
                :fileChangedCallback="handleFileSelected"
              ></upload-btn>
              <v-btn :disabled="csvUsers == null" @click="resetCSVForm" class="flex-grow-1 b100">
                {{ $vuetify.t('$vuetify.AdminUsers.csvReset') }}
              </v-btn>
            </div>
            <div class="d-flex justify-center">
              <a :href="this.csvUserImportSample" download="user_import_sample.csv" class="v-btn theme--light">
                {{ $vuetify.t('$vuetify.AdminUsers.downloadSampleCSV') }}
              </a>
            </div>
          </v-flex>

          <v-flex v-if="csvErrors.length > 0">
            <v-alert v-for="error in csvErrors" :key="error" value="1">
              {{ error }}
            </v-alert>
          </v-flex>

          <v-flex sm12 mb-3 v-if="csvUsers != null">
            <v-data-table
              item-key="username"
              :headers="csvPreviewHeaders"
              :items="csvUsers">
              <v-toolbar slot="header" class="mb-2" flat>
                <v-toolbar-title>
                  {{
                    $vuetify.t('$vuetify.AdminUsers.csvTableHeader',
                      this.csvUsers.length)
                  }}
                </v-toolbar-title>
              </v-toolbar>

              <template slot="items" slot-scope="props">
                <td>{{ props.item.username }}</td>
                <td>{{ props.item.first_name }}</td>
                <td>{{ props.item.last_name }}</td>
                <td>{{ props.item.email }}</td>
              </template>
            </v-data-table>
          </v-flex>

          <v-flex sm12 md6 px-2 v-if="csvUsers != null">
            <v-select
              name="userRole"
              v-model="userRole"
              v-validate="'required'"
              :error-messages="errors.collect('userRole')"
              :items="userRoles"
              :label="this.$vuetify.t('$vuetify.AdminUsers.formGroup')"
              item-text="name"
              item-value="id"
              required/>
          </v-flex>
          <v-flex sm12 md6 px-2 v-if="csvUsers != null">
            <v-select
              name="ideaSpace"
              v-model="ideaSpace"
              v-validate
              :error-messages="errors.collect('ideaSpace')"
              :items="ideaSpaces"
              :label="this.$vuetify.t('$vuetify.AdminUsers.formIdeaSpace')"
            ></v-select>
          </v-flex>
        </v-layout>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          @click="saveCSV"
          :loading="isLoading"
          :disabled="csvUsers == null">
          {{ $vuetify.t('$vuetify.AdminUsers.csvSave') }}
        </v-btn>
        <v-btn
          :disabled="isLoading"
          @click="closeCSVDialog(false)">
          {{ $vuetify.t('$vuetify.AdminUsers.csvClose') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import UploadButton from 'vuetify-upload-button'
import Encoding from 'encoding-japanese'
import Papa from 'papaparse'
import api from '@/api'
import {getIdeaSpaces} from '../../utils/ideaSpaces'
import {getUserRoles} from '../../utils/userRoles'

export default {
  $_veeValidate: {validator: 'new'},
  props: {
    show: Boolean,
    users: Array
  },
  name: 'UserCSVImportDialog',
  data: function () {
    return {
      isLoading: false,
      csvUsers: null,
      csvErrors: [],
      csvUserImportSample: require('../../assets/files/user_import_sample.csv'),
      csvPreviewHeaders: [
        {text: 'Benutzername', value: 'username'},
        {text: 'Vorname', value: 'first_name'},
        {text: 'Nachname', value: 'last_name'},
        {text: 'Email', value: 'email'}
      ],
      ideaSpace: null,
      userRole: null,
      ideaSpaces: [],
      userRoles: []
    }
  },
  beforeMount: async function () {
    this.ideaSpaces = await getIdeaSpaces(this.$store.getters.selected_school, this.$vuetify.t)
    this.userRoles = await getUserRoles()
  },
  computed: {
    isShowing: {
      get () {
        return this.show
      },
      set (value) {
        this.$emit('handleClose', value)
      }
    },
    allUsernames: function () {
      const existing = this.users == null ? [] : this.users.map(u => u.username)
      const fromCsv = this.csvUsers == null ? [] : this.csvUsers.map(u => u.username)
      return existing.concat(fromCsv)
    }
  },
  components: {
    'upload-btn': UploadButton
  },
  methods: {
    handleFileSelected: function (selection) {
      this.resetCSVForm()

      const reader = new FileReader()
      const that = this
      reader.onload = function (e) {
        const codes = new Uint8Array(e.target.result)
        const encoding = Encoding.detect(codes)
        if (encoding === 'UTF8' || encoding === 'ASCII') {
          Papa.parse(selection, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
              if (results.errors.length > 0) {
                results.errors.map(error =>
                  that.csvErrors.push(
                    that.$vuetify.t('$vuetify.AdminUsers.csvGenericError') + ' ' + error.message
                  )
                )
              }

              const headersComplete = ['Vorname', 'Nachname']
                .every(field => results.meta.fields.includes(field))

              if (!headersComplete) {
                that.resetCSVForm()
                that.csvErrors.push(that.$vuetify.t(
                  '$vuetify.AdminUsers.csvFormatError'
                ))
                return
              }

              const entryHasNames = entry =>
                entry['Vorname'].length > 0 &&
                entry['Nachname'].length > 0

              if (!results.data.every(entryHasNames)) {
                that.csvErrors.push(that.$vuetify.t('$vuetify.AdminUsers.csvMissingCellsError'))
              }

              that.csvUsers = results.data
                .filter(entryHasNames)
                .map(entry => ({
                  first_name: entry['Vorname'],
                  last_name: entry['Nachname'],
                  email: entry['Email'],
                  school: that.$store.getters.selected_school,
                  username: that.genUsername(entry['Vorname'], entry['Nachname'])
                }))
            },
            error: (err) => {
              alert(err.message)
              that.resetCSVForm()
              that.showSnackbar = true
              that.snackbarMsg = that.$vuetify.t('$vuetify.AdminUsers.csvGenericError') + ' ' + err.message
            }
          })
        } else {
          that.resetCSVForm()
          that.csvErrors.push(that.$vuetify.t(
            '$vuetify.AdminUsers.csvEncodingError'
          ))
        }
      }
      reader.readAsArrayBuffer(selection)
    },
    saveCSV: function () {
      this.$validator.validate()
        .then(isFormValid => {
          if (!isFormValid) {
            return
          }

          if (this.csvUsers.length === 0) {
            this.snackbarMsg = this.$vuetify.t('$vuetify.AdminUsers.loadingStudentsError')
            this.showSnackbar = true
            return
          }

          const usersWithRoles = this.csvUsers.map(user =>
            Object.assign({}, user, {
              user_role: this.userRole,
              idea_space: this.ideaSpace
            })
          )

          this.isLoading = true
          Promise.all(usersWithRoles.map(api.user.create))
            .then((responses) => {
              this.isLoading = false
              const success = responses.every(res => res.status < 400)

              if (success) {
                this.showSnackbar = true
                this.snackbarMsg = this.$vuetify.t(
                  '$vuetify.AdminUsers.snackbarCreated', responses.length)
                this.closeCSVDialog(true)
                this.handleSuccess()
              } else {
                this.showSnackbar = true
                this.snackbarMsg = this.$vuetify.t(
                  '$vuetify.Snackbar.serverError')
              }
            })
            .catch(() => {
              this.showSnackbar = true
              this.snackbarMsg = this.$vuetify.t(
                '$vuetify.Snackbar.clientError')
            })
        })
    },
    resetCSVForm: function () {
      this.csvUsers = null
      this.csvErrors = []
    },
    closeCSVDialog: function (hasUsersChanged) {
      if (hasUsersChanged) {
        this.$emit('reloadUsers')
      }
      this.isShowing = false
      this.resetCSVForm()
    },
    genUsername: function (first, last) {
      let candidate
      const firstName = first
      const lastName = last

      let stopSearchingUsername = false
      let sliceSize = 3
      let shortFirstname = firstName.slice(0, sliceSize)
      let shortLastname = lastName.slice(0, sliceSize)
      let letterVariant = 97
      let lettersIndex = 0
      while (!stopSearchingUsername) {
        candidate = (shortFirstname + shortLastname).toLowerCase()
        if (!this.allUsernames.includes(candidate)) {
          this.allUsernames.push(candidate)
          return candidate
        } else {
          shortLastname += String.fromCharCode(letterVariant + lettersIndex++)
        }

        if (lettersIndex >= 26) {
          stopSearchingUsername = true
        }
      }

      // Fallback if no unique username can be generated
      // random number between 100 and 999
      return Math.floor((Math.random() * 899) + 100)
    }
  }
}
</script>

<style scoped>

</style>
